import { useSyncExternalStore } from 'react'

// Feature flags — sessionStorage with URL bootstrap.
//
// Bootstrap (once, in main.tsx before React mounts):
//   readUrlFlagsAndPersist(location.search)
//
// Flags then read synchronously from anywhere via:
//   if (isEnabled('schedule')) { … }
//
// Why sessionStorage rather than URL on every request: internal links
// inside v2 surfaces would otherwise have to thread `?v2=schedule`
// through every <Link>. sessionStorage keeps the flag stable across
// navigation within the tab. Closing the tab clears it — so demo
// flags don't linger on the next session.
//
// Escape hatch: `?v2=off` clears all flags in that tab (useful when
// something v2 breaks and the user needs v1 back, no DevTools needed).
//
// React-aware: a tiny pub/sub powers `useFlags()` so the topbar
// indicator updates immediately when the user clicks "Clear".

const FLAG_KEY = 'nymr.v2Flags'

const subscribers = new Set<() => void>()

function notify(): void {
  for (const fn of subscribers) fn()
}

function safeParse(raw: string | null): Record<string, boolean> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, boolean>
    }
  } catch {
    // fall through
  }
  return {}
}

export function getFlags(): Record<string, boolean> {
  if (typeof sessionStorage === 'undefined') return {}
  return safeParse(sessionStorage.getItem(FLAG_KEY))
}

export function isEnabled(flag: string): boolean {
  return !!getFlags()[flag]
}

export function clearFlags(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(FLAG_KEY)
  notify()
}

// Called once in src/main.tsx before React mounts. Reads `?v2=foo&v2=bar`
// from the URL and merges into sessionStorage. `?v2=off` is the kill
// switch — clears everything.
export function readUrlFlagsAndPersist(search: string): void {
  if (typeof sessionStorage === 'undefined') return
  const url = new URLSearchParams(search)
  const values = url.getAll('v2')
  if (values.length === 0) return
  if (values.includes('off')) {
    clearFlags()
    return
  }
  const next = getFlags()
  for (const v of values) {
    const k = v.trim()
    if (k) next[k] = true
  }
  sessionStorage.setItem(FLAG_KEY, JSON.stringify(next))
  notify()
}

// ---- React surface --------------------------------------------------------

// Stable empty snapshot — returning a new {} each call from getFlags()
// would defeat useSyncExternalStore's referential-equality check. We
// memoise the snapshot here.
let cachedSnapshot: Record<string, boolean> = getFlags()
let cachedJson = JSON.stringify(cachedSnapshot)

function getSnapshot(): Record<string, boolean> {
  const fresh = getFlags()
  const json = JSON.stringify(fresh)
  if (json !== cachedJson) {
    cachedSnapshot = fresh
    cachedJson = json
  }
  return cachedSnapshot
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn)
  return () => { subscribers.delete(fn) }
}

// useFlags — subscribes to flag changes. Re-renders when flags are
// cleared via clearFlags(). Use isEnabled() for one-shot reads outside
// React (route guards, etc.).
export function useFlags(): Record<string, boolean> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
